import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import { Therapy } from 'interfaces/therapy';
import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

/**
 * @description Props for the TherapySelector component
 * @typedef {Object} TherapySelectorProps
 * @property {Concentration | null} record - The concentration record being edited or created
 * @property {Control<ConcentrationFormData, any>} control - React Hook Form control object
 * @property {boolean} isConfirmed - Whether the form is in a confirmed/readonly state
 * @property {{label: string, value: string} | null | undefined} watchTherapy - The currently selected therapy value
 * @property {Therapy[]} therapies - Array of available therapies to select from
 * @property {boolean} canManage - Whether the user has permission to manage/edit the therapy
 */
type TherapySelectorProps = {
  record: Concentration | null;
  control: Control<ConcentrationFormData, any>;
  isConfirmed: boolean;
  watchTherapy:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  therapies: Therapy[];
  canManage: boolean;
};

/**
 * @description Component for selecting a therapy in the concentration form.
 * Displays dropdown selector in edit mode or therapy name in readonly mode.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {TherapySelectorProps} props - Component props
 *
 * @returns {JSX.Element | null} Rendered component or null if therapy not required
 */
const TherapySelector: FC<TherapySelectorProps> = ({
  record,
  control,
  isConfirmed,
  watchTherapy,
  therapies,
  canManage,
}) => {
  const { t } = useTranslation();

  return record?.therapy_required ? (
    <div className="customRow">
      <div className="device-concentration-form__label">
        {t('device.therapy')}
      </div>

      {isConfirmed && watchTherapy?.label ? (
        <div className="device-concentration-form__column device-concentration-form__concentration--therapy">
          {watchTherapy?.label}
        </div>
      ) : (
        <div>
          <div className="device-concentration-form__select">
            <Controller
              control={control}
              name="therapy"
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Select
                  id="therapy"
                  maxMenuHeight={300}
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value as any}
                  isMulti={false}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  aria-label="therapy"
                  isClearable
                  isSearchable
                  placeholder="- Select therapy -"
                  options={therapies.map((therapy: Therapy) => ({
                    label: therapy.name,
                    value: therapy.id,
                  }))}
                  isDisabled={!canManage}
                />
              )}
            />
          </div>
          {/* v8 ignore start -- WAIVER(DEFENSIVE): a concentration cannot be confirmed without its therapy, so a confirmed record with an incomplete therapy is a data-integrity fail-safe surface only */}
          {record.is_complete && !record.is_complete_therapy ? (
            <div className="invalid-feedback pt-1">
              {t('device.errors.therapy__required')}
            </div>
          ) : null}
          {/* v8 ignore stop -- WAIVER(DEFENSIVE) */}
        </div>
      )}
    </div>
  ) : null;
};

export default TherapySelector;
